/* ==========================================================================
   Start Nutra · Molecule ingredients row: desktop scroll-jack + fallbacks
   --------------------------------------------------------------------------
   PORTED from assets/sn-reviews-scroll.js (sn-home-reviews-scroll's
   pinned horizontal scroll-jack), per the founder's explicit instruction
   to reuse that mechanism rather than write a second one. The physics,
   wheel-delta normalisation, wheel-granularity pin-engage budget, header-
   clearance offset formula, and the single-getBoundingClientRect
   performance constraint are all unchanged from that file, see its own
   header comment for the full history of why each of those exists (wheel
   deltaMode bug, the 100vh/dead-scroll bug, etc.). Only the following
   were adapted for this component, deliberately, not by omission:

   1. ADDITIONAL (min-width: 1024px) GATE. Reviews (a homepage section) is
      gated purely on (hover: hover) and (pointer: fine). This task's own
      spec is explicit: "Below 1024px do not pin at all, pinning fights
      native momentum scrolling on phones." So MODE_A_QUERY below adds
      that width gate on top of the same capability check, in both this
      file and the matching CSS media queries in
      section-sn-pdp-molecule-ingredients.css, not a new approach, the
      same one with one more AND clause.

   2. NO CARD SCALE/OPACITY EFFECT. Reviews' render loop also scales/fades
      cards by distance from centre (updateCardScale). This task's spec
      says the cards themselves, and the row's contents, stay strictly
      unchanged, so that visual effect (which changes how cards look
      during the interaction) was left out entirely: this file drives
      translateX only.

   3. NO initHeaderHeightSync HERE. Reviews duplicates that function
      because the homepage never otherwise keeps --header-height current.
      This section only ever renders on PDP templates, which already run
      it via assets/sn-pdp-gallery.js (loaded by sn-pdp-hero, present on
      every product template ahead of this section), so --header-height
      is already live by the time this file needs it. Re-duplicating the
      same ResizeObserver on the same header element a second time would
      just be redundant, not more correct.

   4. NO LAZY-LOAD FIX. Reviews' forceEagerLoadNearViewport works around
      native `loading="lazy"` on <img> not re-triggering for a
      transform-revealed card. This section has no <img> tags at all,
      every molecule diagram is inline SVG, rendered directly into the
      DOM, so that failure mode does not exist here and nothing was
      ported for it.

   Everything else, the three-mode split (A/B transform-driven +
   pinned/unpinned, C native fallback), the lerp-smoothed vertical-wheel
   jack with its zone check and release-at-either-end behaviour, drag-
   scroll, horizontal wheel/trackpad, and mode C's keyboard support, is
   the same mechanism, same constants, same measure()/loop() structure.
   ========================================================================== */
(function () {
  'use strict';

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  // --header-height is kept current by assets/sn-pdp-gallery.js's
  // initHeaderHeightSync, already running on every PDP page ahead of this
  // section (see header comment, point 3): read only, never set here.
  // Same formula as sn-reviews-scroll.js's getPinTopOffset, for the same
  // reason: --sn-space-lg matches the PDP hero's own header-clearance
  // margin, for consistency across the theme.
  function getPinTopOffset() {
    var cs = getComputedStyle(document.documentElement);
    var headerH = parseFloat(cs.getPropertyValue('--header-height')) || 0;
    var marginH = parseFloat(cs.getPropertyValue('--sn-space-lg')) || 24;
    return headerH + marginH;
  }

  // Capability check, matched independently in CSS via the identical
  // (min-width: 1024px) and (hover: hover) and (pointer: fine) media
  // condition, checked once here, not live, matching sn-reviews-scroll.js's
  // existing convention.
  var supportsScrollJack = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var LERP_FACTOR = 0.15;
  var LERP_SNAP_PX = 0.05;
  // Same rationale as sn-reviews-scroll.js's PIN_ENGAGE_BUDGET_PX: sticky
  // needs some travel to pin at all, and one wheel event has to land
  // inside the pin band for the jack to engage: a wheel-granularity
  // budget, not one derived from the row's own horizontal travel.
  var PIN_ENGAGE_BUDGET_PX = 240;
  var DRAG_THRESHOLD = 4;   // px of mouse movement before a mousedown counts as a drag, not a click
  var KEY_SCROLL_STEP = 240; // px per Arrow Left/Right press (mode C only)
  var WHEEL_LINE_HEIGHT_PX = 16; // DOM_DELTA_LINE -> px conversion, see normalizeWheelDelta below

  // See sn-reviews-scroll.js's identical function for the full bug
  // history (WheelEvent.deltaX/deltaY are not always pixels).
  function normalizeWheelDelta(value, deltaMode) {
    if (deltaMode === 1) return value * WHEEL_LINE_HEIGHT_PX; // DOM_DELTA_LINE
    if (deltaMode === 2) return value * window.innerHeight;   // DOM_DELTA_PAGE
    return value; // DOM_DELTA_PIXEL: already correct as-is
  }

  // ==========================================================================
  // MODE A / MODE B: transform-driven row
  // ==========================================================================

  function SnMoleculesRowTransform(wrapper) {
    this.wrapper = wrapper;
    this.sticky = wrapper.querySelector('.sn-pdp-molecule-ingredients__sticky');
    this.row = wrapper.querySelector('.sn-pdp-molecule-ingredients__row');
    this.cards = this.row ? Array.prototype.slice.call(this.row.querySelectorAll('.sn-pdp-molecule-ingredients__card')) : [];
    if (!this.sticky || !this.row || !this.cards.length) return;

    this.pinned = !reducedMotion; // mode A vs mode B, see header note

    this.targetOffset = 0;
    this.renderedOffset = 0;
    this.maxOffset = 0;
    this.stickyWidth = 0;
    this.stickyHeight = 0;
    this.cardLefts = [];
    this.cardWidths = [];

    this.isDown = false;
    this.dragged = false;
    this.startX = 0;
    this.startTargetOffset = 0;

    this.measure = this.measure.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.loop = this.loop.bind(this);

    this.measure();
    window.addEventListener('resize', this.measure, { passive: true });

    this.wrapper.addEventListener('wheel', this.onWheel, { passive: false });
    this.row.addEventListener('mousedown', this.onMouseDown);
    // move/up on window, not the row: a fast drag that leaves the
    // element must not get stuck "down".
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    this.row.addEventListener('click', this.onClickCapture, true);

    requestAnimationFrame(this.loop);
  }

  // Zero getBoundingClientRect calls here: the only one in this file
  // lives in onWheel, on the wrapper, reached only for a vertical-
  // dominant wheel event. Sticky width/height and card left-offsets all
  // use offsetWidth/offsetHeight/offsetLeft instead, read once per
  // load/resize and cached for the render loop and onWheel's zone check
  // to use without re-measuring anything. See sn-reviews-scroll.js's
  // identical function for the full derivation of contentWidth/maxOffset.
  SnMoleculesRowTransform.prototype.measure = function () {
    this.stickyWidth = this.sticky.offsetWidth;
    this.stickyHeight = this.sticky.offsetHeight;

    var lefts = [];
    var widths = [];
    for (var i = 0; i < this.cards.length; i++) {
      lefts.push(this.cards[i].offsetLeft);
      widths.push(this.cards[i].offsetWidth);
    }
    this.cardLefts = lefts;
    this.cardWidths = widths;

    var last = this.cards.length - 1;
    var rowPad = lefts[0] - this.row.offsetLeft; // cards and row share an offsetParent (.sticky when pinned, .wrapper otherwise)
    var contentWidth = lefts[last] + widths[last] - this.row.offsetLeft + rowPad;

    this.maxOffset = Math.max(0, contentWidth - this.stickyWidth, this.row.scrollWidth - this.stickyWidth);
    this.targetOffset = clamp(this.targetOffset, 0, this.maxOffset);
    this.renderedOffset = clamp(this.renderedOffset, 0, this.maxOffset);

    if (this.pinned && this.maxOffset > 0) {
      this.wrapper.style.height = (this.stickyHeight + PIN_ENGAGE_BUDGET_PX) + 'px';
    } else {
      this.wrapper.style.height = ''; // mode B (and mode A with nothing to traverse): natural content flow, no pin travel
    }
  };

  SnMoleculesRowTransform.prototype.onWheel = function (e) {
    var deltaX = normalizeWheelDelta(e.deltaX, e.deltaMode);
    var deltaY = normalizeWheelDelta(e.deltaY, e.deltaMode);
    var horizontalGesture = Math.abs(deltaX) > Math.abs(deltaY);

    if (horizontalGesture) {
      // Trackpad swipe / shift+wheel / horizontal wheel: ALWAYS active,
      // not gated by zone, same standing as drag.
      e.preventDefault();
      this.targetOffset = clamp(this.targetOffset + deltaX, 0, this.maxOffset);
      this.renderedOffset = this.targetOffset; // instant: already a smooth native gesture
      return;
    }

    if (!this.pinned) return; // mode B: no vertical wheel-jacking at all, page scrolls normally

    // Vertical gesture: the one getBoundingClientRect call in this file.
    var rect = this.wrapper.getBoundingClientRect();
    var pinTopOffset = getPinTopOffset();
    var inZone = rect.top <= pinTopOffset && rect.bottom > (pinTopOffset + this.stickyHeight);
    if (!inZone) return; // let native scroll pass through

    var scrollingDown = deltaY > 0;
    var roomToMove = scrollingDown ? (this.targetOffset < this.maxOffset) : (this.targetOffset > 0);
    if (!roomToMove) return; // no room in that direction: let native scroll pass through, which releases the pin

    e.preventDefault();
    this.targetOffset = clamp(this.targetOffset + deltaY, 0, this.maxOffset);
    // renderedOffset intentionally left alone here: the render loop's lerp catches up to it
  };

  SnMoleculesRowTransform.prototype.onMouseDown = function (e) {
    if (e.button !== 0) return; // left button only
    this.isDown = true;
    this.dragged = false;
    this.startX = e.clientX;
    this.startTargetOffset = this.targetOffset;
    this.row.classList.add('sn-pdp-molecule-ingredients__row--dragging');
  };

  SnMoleculesRowTransform.prototype.onMouseMove = function (e) {
    if (!this.isDown) return;
    var dx = e.clientX - this.startX;
    if (!this.dragged && Math.abs(dx) > DRAG_THRESHOLD) this.dragged = true;
    if (!this.dragged) return;
    e.preventDefault();
    this.targetOffset = clamp(this.startTargetOffset - dx, 0, this.maxOffset);
    this.renderedOffset = this.targetOffset;
  };

  SnMoleculesRowTransform.prototype.onMouseUp = function () {
    if (!this.isDown) return;
    this.isDown = false;
    this.row.classList.remove('sn-pdp-molecule-ingredients__row--dragging');
  };

  SnMoleculesRowTransform.prototype.onClickCapture = function (e) {
    if (!this.dragged) return;
    e.preventDefault();
    e.stopPropagation();
    this.dragged = false;
  };

  // Pure arithmetic against this.targetOffset, no card scale/opacity
  // effect (see header comment, point 2) and no getBoundingClientRect
  // calls in this function.
  SnMoleculesRowTransform.prototype.loop = function () {
    if (this.pinned) {
      var delta = this.targetOffset - this.renderedOffset;
      if (Math.abs(delta) < LERP_SNAP_PX) {
        this.renderedOffset = this.targetOffset;
      } else {
        this.renderedOffset += delta * LERP_FACTOR;
      }
    } else {
      this.renderedOffset = this.targetOffset; // mode B: instant, no lerp
    }

    this.row.style.transform = 'translateX(' + (-this.renderedOffset) + 'px)';

    requestAnimationFrame(this.loop);
  };

  // ==========================================================================
  // MODE C: native fallback (touch / coarse pointer / no hover capability
  // / narrower than 1024px)
  // ==========================================================================

  function SnMoleculesRowNative(row) {
    this.el = row;
    this.isDown = false;
    this.dragged = false;
    this.startX = 0;
    this.startScrollLeft = 0;
    this.reducedMotion = reducedMotion;

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.el.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    this.el.addEventListener('click', this.onClickCapture, true);
    this.el.addEventListener('keydown', this.onKeyDown);
  }

  SnMoleculesRowNative.prototype.onMouseDown = function (e) {
    if (e.button !== 0) return;
    this.isDown = true;
    this.dragged = false;
    this.startX = e.clientX;
    this.startScrollLeft = this.el.scrollLeft;
    this.el.classList.add('sn-pdp-molecule-ingredients__row--dragging');
    this.el.focus({ preventScroll: true });
  };

  SnMoleculesRowNative.prototype.onMouseMove = function (e) {
    if (!this.isDown) return;
    var dx = e.clientX - this.startX;
    if (!this.dragged && Math.abs(dx) > DRAG_THRESHOLD) this.dragged = true;
    if (!this.dragged) return;
    e.preventDefault();
    this.el.scrollLeft = this.startScrollLeft - dx;
  };

  SnMoleculesRowNative.prototype.onMouseUp = function () {
    if (!this.isDown) return;
    this.isDown = false;
    this.el.classList.remove('sn-pdp-molecule-ingredients__row--dragging');
  };

  SnMoleculesRowNative.prototype.onClickCapture = function (e) {
    if (!this.dragged) return;
    e.preventDefault();
    e.stopPropagation();
    this.dragged = false;
  };

  SnMoleculesRowNative.prototype.onKeyDown = function (e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    var step = e.key === 'ArrowRight' ? KEY_SCROLL_STEP : -KEY_SCROLL_STEP;
    if (this.reducedMotion) {
      this.el.scrollLeft += step;
    } else {
      this.el.scrollBy({ left: step, behavior: 'smooth' });
    }
  };

  function init() {
    document.querySelectorAll('[data-sn-molecules-wrapper]').forEach(function (wrapper) {
      if (supportsScrollJack) {
        new SnMoleculesRowTransform(wrapper);
      } else {
        var row = wrapper.querySelector('.sn-pdp-molecule-ingredients__row');
        if (row) new SnMoleculesRowNative(row);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
